/*! playlist-player.js */

import AdEvents from "app/events/ad-events";
import FullScreen from "app/utils/fullscreen";
import EventTarget from "app/utils/event-target";
import FacadePlayer from "../../facade/facade-player";
import PlayerEvents from "app/events/player-events";
import PlaylistEvents from "app/events/playlist-events";
import PlaylistView from "./playlist-view";
import TransitionView from "./transition-view";
import PersonalizationService from "../../services/personalization-service";
import StrategyTypes from "../../strategy/StrategyTypes";
import ProjectorUtils from "app/utils/projector-utils";
import PosterSize from "app/lib/projector-settings-js/poster-size";
import Log from "app/utils/log";
import ErrorReport from "app/utils/error-report";
import CSSHelper from "app/utils/css-helpers";
import { AUTOPLAY_CLASS } from "app/utils/Constant";
import { getAspectRatio } from "../../utils/aspectRatioUtils";
const log = new Log("PlaylistPlayer");
const MAX_UP_NEXT_ITEMS = 2;
const BLOCKED_EVENTS = {
    [PlayerEvents.PLAY]: true,
    [PlayerEvents.PAUSE]: true,
    [PlayerEvents.TRANSCRIPT_ON]: true,
    [PlayerEvents.TRANSCRIPT_OFF]: true,
    [PlayerEvents.TRANSCRIPT_READY]: true,
    [PlayerEvents.TIMEUPDATE]: true,
    [PlayerEvents.WILL_PLAY]: true
};
const NOOP = function() {
};

/**
 * @typedef {object} PlaylistOptions
 * @param {boolean} loadSuggestions Flag indicating that suggestions should be loaded from personalization endpoint
 * @param {boolean} repeat
 * @param {number} preloadThreshold Threshold in seconds before next video should preload
 * @param {number} startFrom Playback starting index
 */
const DEFAULT_OPTIONS = {
    loadSuggestions: true,
    preloadThreshold: 10,
    repeat: false,
    startFrom: 0,
    onReady: NOOP,
    onError: NOOP,
    transitionDuration: 5000
};

const OUTRO_PLUGIN = "PlaybackOutro";

const BOUND_HANDLERS = [
    "getSuggestions",
    "onCancelTransition",
    "onFullScreenChange",
    "onSlideClick",
    "onResumeRequest",
    "onTimeUpdate",
    "onVideoEnd",
    "onVideoPause",
    "onVideoPlay",
    "toggleFullScreen",
    "_onTranscriptOn",
    "_onTranscriptOff",
    "_onTranscriptReady",
    "_onWillPlay"
];

const SLIDES = ".bvp-playlist-slide";
const PRELOAD_SLIDE_CLASS = "bvp-playlist-slide__preload";
const TRANSITION_STYLE = "next-slide-transition";
const TRANSITION_END = "transitionend";
const LAST_VIDEO_CLASS = "bvp-playlist-player--last";

/**
 * @class PlaylistPlayer
 * @implements {EventTarget}
 */
class PlaylistPlayer extends EventTarget {

    /**
     * @constructs PlaylistPlayer
     * @param {Node} containerElement
     * @param {object | object[]} playlist One or more videos
     * @param {PlaylistOptions} options
     */
    constructor(containerElement, playlist = [], options = {}) {
        super();

        this.options = Object.assign({}, DEFAULT_OPTIONS, options);
        this.bmmrIds = {};
        this.currentItem = this.options.startFrom;
        this.currentId = null;
        this.currentPlayer = null;
        this.mayLoadSuggestions = false;
        this.personalizationService = new PersonalizationService(options.recommendationOptions);
        this.playlist = [];
        this.playlistItems = this.playlist.length;
        this.playlistView = new PlaylistView();
        this.preloadingNext = false;
        this.preloadItem = null;
        this.preloadPlayer = null;
        this.transitionView = new TransitionView(options.transitionDuration);
        this.containerElement = containerElement;

        /**
         * Allow bubbling of events from views to PlaylistPlayer
         */
        this.playlistView.setEventParent(this);
        this.transitionView.setEventParent(this);

        BOUND_HANDLERS.forEach((handler) => {
            this[handler] = this[handler].bind(this);
        });

        /**
         * Add playlist items
         */
        this.add(playlist);
        this.hasRecommendations = this.playlistItems >= 2;
        this.mayLoadSuggestions = this.options.loadSuggestions && this.playlistItems === 1;

        /**
         * Configure transition view
         */
        this.transitionView.hide();
        this.transitionView
            .on(PlaylistEvents.TRANSITION_END, this.playNext.bind(this, true))
            .on(PlaylistEvents.TRANSITION_SKIP, this.playNext.bind(this, false))
            .on(PlaylistEvents.TRANSITION_CANCEL, this.onCancelTransition);

        this.playlistView.appendView(this.transitionView, ".bvp-playlist-player__container");
        this.playlistView.on(PlayerEvents.FULL_SCREEN_CHANGE, this.onFullScreenChange);

        this.transitionView.el().style.visibility = "hidden";

        const slides = this.playlistView.findAll(SLIDES);
        this.primarySlide = slides[0];
        this.preloadSlide = slides[1];

        this.containerElement.appendChild(this.playlistView.el());
        this.startPlayback();
    }

    getContentOverlayEl() {
        return this.currentPlayer?.getContentOverlayEl();
    }

    /**
     * Remove all recommended videos from the playlist
     * @method
     */
    resetPlaylist(newPlaylistItems) {
        this.playlist = [];
        this.bmmrIds = {};
        this.playlistItems = 0;

        this.currentItem = -1;

        const currentVideoBmmrId = this.getCurrentId();
        for (let i = 0; i < newPlaylistItems.length; i++) {
            const newPlaylistItem = newPlaylistItems[i];
            if (currentVideoBmmrId === newPlaylistItem.id) {
                this.currentItem = i;
                break;
            }
        }

        if (this.currentItem === -1) {
            newPlaylistItems.unshift(this.currentPlayer.userOptions);
            this.currentItem = 0;
        }

        this.add(newPlaylistItems);

        this.hasRecommendations = newPlaylistItems.length > 1;
    }

    /**
     * Adds video to the playlist (filters-out duplicates)
     * @method
     * @param {object|object[]} item
     */
    add(item = {}) {
        const items = Array.isArray(item) ? item : [item];

        items.forEach((video) => {
            if (video.id && !this.bmmrIds[video.id]) {
                this.playlist.push(video);
                video.playlistIndex = this.playlist.length - 1;
                this.bmmrIds[video.id] = 1;
            }
        });

        this.playlistItems = this.playlist.length;
    }

    /**
     * @method
     * @param {number} index
     * @param {Boolean} [isAutoContinuousPlay=false]
     * @returns {Promise.<Player>}
     */
    loadVideo(index, isAutoContinuousPlay = false) {
        const playlistItem = prepareItem(this.playlist[index], {
            containerElement: this.primarySlide,
            height: this.options.height,
            width: this.options.width
        });

        this.trigger(PlaylistEvents.ITEM_LOAD, { index });
        this.currentItem = index;
        this.currentPlayer = this._createPlayer(playlistItem);
        this.currentId = playlistItem.id;

        this._attachLastVideoState();

        if (isAutoContinuousPlay) {
            this.currentPlayer.trigger(PlayerEvents.CONTINUOUS_PLAY_START);
        }

        return this.currentPlayer
            .ready()
            .then(disableOutroPlugin)
            .then((player) => {
                if (player) {
                    this.transitionView.el().style.visibility = "";
                    this.setTechStyle(player.techName_);

                    if (FullScreen.isSupported()) {
                        player.requestFullscreen = this.toggleFullScreen;
                        player.isFullscreen_ = this.playlistView.isFullScreen;
                    }

                    this._registerPlayerEvents(this.currentPlayer);
                }

                return player;
            });
    }

    _registerPlayerEvents(player) {
        if (player.transcript()) {
            this._onTranscriptReady();
        }

        player
            .on(PlayerEvents.ENDED, this.onVideoEnd)
            .on(PlayerEvents.TIMEUPDATE, this.onTimeUpdate)
            .on(PlayerEvents.PAUSE, this.onVideoPause)
            .on([PlayerEvents.PLAY, AdEvents.AD_STARTED], this.onVideoPlay)
            .on(PlayerEvents.TRANSCRIPT_ON, this._onTranscriptOn)
            .on(PlayerEvents.TRANSCRIPT_OFF, this._onTranscriptOff)
            .on(PlayerEvents.TRANSCRIPT_READY, this._onTranscriptReady)
            .on(PlayerEvents.WILL_PLAY, this._onWillPlay);
    }

    /**
     * @method
     * @returns {Promise.<Player>}
     */
    preloadNext() {
        const nextIndex = this.getNextIndex();
        this.preloadingNext = true;

        if (nextIndex === undefined) {
            enableOutroPlugin(this.currentPlayer.player);
            return;
        }

        log.warn("Preloading next item:", nextIndex);
        this.preloadItem = nextIndex;

        const playlistItem = prepareItem(this.playlist[nextIndex], {
            containerElement: this.preloadSlide,
            autoplay: false,
            height: this.options.height,
            preload: "auto",
            width: this.options.width,
            isPlaylistPreloadedPlayer: true
        });

        this.trigger(PlaylistEvents.ITEM_PRELOAD, { index: nextIndex });
        this.preloadPlayer = this._createPlayer(playlistItem);
        return this.preloadPlayer
            .ready()
            .then(disableOutroPlugin)
            .then((player) => {
                if (player) {
                    if (FullScreen.isSupported()) {
                        player.requestFullscreen = this.toggleFullScreen;
                        player.isFullscreen_ = this.playlistView.isFullScreen;
                    }
                }

                return player;
            })
            .catch(error => ErrorReport.recordGenericError(log, "Unable to preload video", error));
    }

    /**
     * @method
     */
    unloadVideo() {
        this.trigger(PlaylistEvents.ITEM_UNLOAD, { index: this.currentItem });
        this.preloadingNext = false;
        try {
            this.currentPlayer.dispose();
        } catch (error) {
            ErrorReport.recordGenericError(log, "Failed to dispose a playlist item:", error);
        }
    }

    /**
     * @method
     */
    play() {
        if (this.currentPlayer) {
            this.currentPlayer.play();
        }
    }

    /**
     * @method
     */
    pause() {
        if (this.currentPlayer) {
            this.currentPlayer.pause();
        }
    }

    /**
     * @method
     */
    replay() {
        if (this.playlistItems) {
            this.unloadVideo();
            this.currentItem = 0;
            this.currentId = null;
            this.startPlayback();
        }
    }

    /**
     * @method
     * @param {function} callback
     * @returns {?Promise}
     */
    ready(callback) {
        if (this.currentPlayer) {
            return this.currentPlayer.ready(callback);
        }
    }

    transcriptOn() {
        if (this.currentPlayer) {
            this.currentPlayer.transcriptOn();
        }
    }

    transcriptOff() {
        if (this.currentPlayer) {
            this.currentPlayer.transcriptOff();
        }
    }

    transcript() {
        if (this.currentPlayer) {
            return this.currentPlayer.transcript();
        }
    }

    currentTime() {
        if (this.currentPlayer) {
            return this.currentPlayer.currentTime();
        }
    }

    /**
     * @method
     * @param {number} index
     */
    playIndex(index = -1) {
        index = +index;
        if (this.playlistItems && index >= 0 && index < this.playlistItems) {
            this._forcePlayIndex(index);
        }
    }

    /**
     * @method
     * @param {string} bmmrId
     */
    playId(bmmrId) {
        for (let i = 0, item; i < this.playlistItems; i++) {
            item = this.playlist[i];
            if (item.id === bmmrId) {
                this.playIndex(i);
                break;
            }
        }
    }

    /**
     * @method
     * @returns {string}
     */
    getCurrentId() {
        return this.currentId;
    }

    /**
     * @method
     * @param {object} item
     */
    setAutoplay(item) {
        item.autoplay = true;
    }

    /**
     * @method
     * @returns {Promise}
     */
    startPlayback() {
        if (!this.playlistItems) {
            return Promise.reject();
        }

        return this.loadVideo(this.currentItem)
            .then((player) => {
                this.trigger(PlaylistEvents.STARTED);
                this.options.onReady(player);
                return player;
            })
            .then(this.getSuggestions)
            .catch((error) => {
                this.options.onError(error);
                ErrorReport.recordGenericError(log, "Failed to start playback:", error);
            });
    }

    /***
     * @method
     * @returns {FacadePlayer}
     */
    getCurrentPlayer() {
        return this.currentPlayer;
    }

    /**
     * @method
     * @returns {number}
     */
    getNextIndex() {
        const nextIndex = this.currentItem + 1;
        const upperLimit = this.playlistItems;

        if (nextIndex >= upperLimit) {
            return this.options.repeat ? 0 : undefined;
        }

        return nextIndex;
    }

    /**
     * @method
     * @param {number} count
     * @returns {array}
     */
    getNextItems(count) {
        const nextIndex = this.getNextIndex();
        return this.options.repeat ? cyclicSlice(this.playlist, nextIndex, count) :
            this.playlist.slice(nextIndex, nextIndex + count);
    }

    /**
     * @method
     * @returns {number}
     */
    getPrevIndex() {
        const prevIndex = this.currentItem - 1;
        const lowerLimit = 0;

        if (prevIndex < lowerLimit) {
            return this.options.repeat ? this.playlistItems - 1 : undefined;
        }

        return prevIndex;
    }

    /**
     * @method
     * @param {Player} player
     * @returns {Promise.<Array>}
     */
    getSuggestions(player) {
        if (this.mayLoadSuggestions) {
            const itemProperties = this.playlist[0];
            const compressedID = player.options_.metadata.compressedID;
            const currentVideo = `Video|${ compressedID }`;
            const requestOptions = {
                exclude: currentVideo,
                currentResource: currentVideo
            };

            return this.personalizationService.getRecommendations(requestOptions, itemProperties).then((recommendations) => {
                this.hasRecommendations = Boolean(recommendations.length);
                this.add(recommendations);
                return recommendations;
            });
        }

        return Promise.resolve(this.playlist);
    }

    /**
     * @method
     * @returns {Promise}
     */
    transitionToPreloadSlide() {
        return new Promise((accept) => {
            const isTransitionSupported = (this.preloadSlide.style.transition !== undefined);

            /**
             * Workaround for IE9
             */
            if (!isTransitionSupported) {
                accept();
                return;
            }

            const transitionEnd = onTransitionEnd.bind(this.preloadSlide, accept, () => transitionEnd);
            this.preloadSlide.addEventListener(TRANSITION_END, transitionEnd);
            this.playlistView.addClass(TRANSITION_STYLE);
        });
    }

    /**
     * Forwards trigger on to current player in addition to PlaylistPlayer itself
     * @override
     * @param {string} type
     * @param {object} eventDetail
     */
    trigger(type, eventDetail) {
        if (this.currentPlayer && !PlaylistEvents[type] && !BLOCKED_EVENTS[type]) {
            this.currentPlayer.trigger(type, eventDetail);
        }

        super.trigger(type, eventDetail);
    }

    /**
     * @method
     * @param {Boolean} [isAutoContinuousPlay=false]
     * @returns {Promise}
     */
    playNext(isAutoContinuousPlay = false) {
        this.transitionView.hide();

        if (this.preloadPlayer) {
            this.currentId = this.preloadPlayer.userOptions.id;

            return this.preloadPlayer.ready().then(() => {

                this.trigger(PlayerEvents.VIDEO_CHANGE, {
                    newVideoIndex: this.preloadPlayer.getPlaylistIndex()
                });

                this.preloadPlayer.setAutoplay(true);

                if (isAutoContinuousPlay) {
                    this.preloadPlayer.trigger(PlayerEvents.CONTINUOUS_PLAY_START);
                }

                this.preloadPlayer.play();

                this.transitionToPreloadSlide().then(() => {
                    this.unloadVideo();
                    this.swapSlides();
                    this.playlistView.removeClass(TRANSITION_STYLE);
                    this._registerPlayerEvents(this.currentPlayer);
                });
            });
        }

        const nextIndex = this.getNextIndex();

        if (nextIndex === undefined) {
            this.onEndOfPlaylist();
            return;
        }

        this._forcePlayIndex(nextIndex, isAutoContinuousPlay);
    }

    /**
     * @method
     */
    playPrevious() {
        const prevIndex = this.getPrevIndex();

        if (prevIndex === undefined) {
            log.warn("Reached the beginning of the playlist");
            return;
        }

        this._forcePlayIndex(prevIndex);
    }

    /**
     * @method
     */
    swapSlides() {
        this.playlistView.removeClass(PRELOAD_SLIDE_CLASS, this.preloadSlide);
        this.playlistView.addClass(PRELOAD_SLIDE_CLASS, this.primarySlide);

        let currentPrime = this.primarySlide;
        let currentPreload = this.preloadSlide;

        this.primarySlide = currentPreload;
        this.preloadSlide = currentPrime;

        this.currentPlayer = this.preloadPlayer;
        this.currentItem = this.preloadItem;
        this.currentId = this.preloadPlayer.userOptions.id;

        if (this.currentPlayer.player) {
            this.currentPlayer.player.isFullscreen_ = this.playlistView.isFullScreen;
        }

        this.preloadPlayer = this.preloadItem = currentPrime = currentPreload = null;
    }

    /**
     * @method
     * @param {string} tech
     */
    setTechStyle(tech) {
        const mobile = ProjectorUtils.isMobile() ? "-mobile" : "";
        this.playlistView.addClass(`vjs-tech-${ tech }${ mobile }`.toLowerCase());
    }

    /**
     * @method
     */
    toggleFullScreen() {
        if (!this.playlistView.isFullScreen) {
            this.playlistView.requestFullScreen();
        } else {
            this.playlistView.exitFullScreen();
        }
    }

    /**
     * @override Player.requestFullscreen;
     * @method
     */
    onFullScreenChange(event) {
        this.currentPlayer.player.isFullscreen_ = event.detail.fullscreen;
        this.currentPlayer.player.trigger(PlayerEvents.FULL_SCREEN_CHANGE);
    }

    /**
     * @method
     */
    onEndOfPlaylist() {
        log.warn("End of playlist reached");
        this.trigger(PlaylistEvents.ALL_ITEMS_PLAYED);
    }

    onVideoEnd() {
        this.preloadingNext = false;

        if (!this.hasRecommendations && !this.options.repeat) {
            this.onEndOfPlaylist();
            return;
        }

        if (this.preloadPlayer) {
            this.preloadPlayer.ready().then((player) => {
                if (!player) {
                    ErrorReport.recordGenericError(log, "Unable to get preload player");
                    return;
                }
                this.transitionView.setTitle(player.options_.title);
                this.transitionView.setPoster(player.options_.posterUrl);
                this.transitionView.setId(player.options_.original_user_options.id);
            });
        }

        const nextIndex = this.getNextIndex();
        if (nextIndex !== undefined) {
            const nextVideo = this.playlist[nextIndex];
            const currentVideoPlayer = this.currentPlayer.player;
            const aspectRatio = getAspectRatio(currentVideoPlayer.bmmrAsset, currentVideoPlayer.userOptions?.aspectRatio());
            const posterUrl = PosterSize.closestImage(currentVideoPlayer.currentWidth(), currentVideoPlayer.currentHeight(), nextVideo.poster, aspectRatio);
            this.transitionView.setTitle(nextVideo.title);
            this.transitionView.setPoster(posterUrl);
            this.transitionView.setId(nextVideo.id);
        }

        this.transitionView.show();
    }

    /**
     * @method
     */
    onTimeUpdate() {
        this.trigger(PlayerEvents.TIMEUPDATE);

        const currentTime = this.currentPlayer.player.currentTime();
        const duration = this.currentPlayer.player.duration();
        const elapsedTime = duration - currentTime;
        const isWithinThreshold = elapsedTime && (elapsedTime < this.options.preloadThreshold);
        if (isWithinThreshold && !this.preloadingNext && !this.currentPlayer.player.ended()) {
            return this.preloadNext();
        }
        return Promise.resolve();
    }

    /**
     * @method
     */
    onVideoPause() {
        if (this.currentPlayer.player.ended()) {
            return;
        }

        const recommendations = this.getNextItems(MAX_UP_NEXT_ITEMS);

        this.hasRecommendations = Boolean(recommendations.length);
        this.trigger(PlayerEvents.PAUSE, { player: this.currentPlayer.player });
    }

    /**
     * @method
     */
    onVideoPlay() {
        this.trigger(PlayerEvents.PLAY, { player: this.currentPlayer.player });
    }

    /**
     * @method
     */
    onCancelTransition() {
        const { player: vjsPlayer } = this.currentPlayer;
        CSSHelper.removeClass(vjsPlayer.el(), AUTOPLAY_CLASS);
        showTitleBar(vjsPlayer);
        this.transitionView.hide();
    }

    /**
     * @method
     */
    onSlideClick(event) {
        const id = event.detail.id;
        this.currentPlayer.trigger({ type: event.type, data: event.detail });
        this.playId(id);
    }

    /**
     * @method
     */
    onResumeRequest() {
        this.currentPlayer.play();
    }

    /**
     * @override
     * @method
     */
    dispose() {
        this.unloadVideo();
        this.removeAllEventListeners();
        this.playlistView.dispose();
        this.currentPlayer = this.preloadPlayer = this.options = this.playlist = null;
        this.playlistView = this.transitionView = this.primarySlide = this.preloadSlide = null;

        this.containerElement.innerHTML = "";

        super.dispose();
    }

    _forcePlayIndex(index, isAutoContinuousPlay = false) {
        this.trigger(PlayerEvents.VIDEO_CHANGE, {
            newVideoIndex: index
        });

        this.unloadVideo();
        const item = this.playlist[index];
        this.setAutoplay(item);
        this.loadVideo(index, isAutoContinuousPlay);
    }

    _attachLastVideoState() {
        const isLastVideo = this.getNextIndex() === undefined;
        if (isLastVideo) {
            this.playlistView.addClass(LAST_VIDEO_CLASS);
        } else {
            this.playlistView.removeClass(LAST_VIDEO_CLASS);
        }
    }

    _createPlayer(options) {
        if (this.currentPlayer) {
            this._updateCorrelator();
        }

        return new FacadePlayer(options, null, StrategyTypes.EMBED);
    }

    _updateCorrelator() {
        const { googletag } = window;
        if (googletag && googletag.cmd && googletag.cmd.push) {
            googletag.cmd.push(() => {
                googletag.pubads().updateCorrelator();
            });
        }
    }

    _onTranscriptOn() {
        this.trigger(PlayerEvents.TRANSCRIPT_ON);
    }

    _onTranscriptOff() {
        this.trigger(PlayerEvents.TRANSCRIPT_OFF);
    }

    _onTranscriptReady() {
        this.trigger(PlayerEvents.TRANSCRIPT_READY);
    }

    _onWillPlay() {
        this.trigger(PlayerEvents.WILL_PLAY);
    }
}

/**
 * @private
 * @param {videojs.Player} player
 * @returns {videojs.Player}
 */
function disableOutroPlugin(player) {
    if (player && player.hasOwnProperty(OUTRO_PLUGIN)) {
        player[OUTRO_PLUGIN].disable();
    }
    return player;
}

/**
 * @private
 * @param {videojs.Player} player
 * @returns {videojs.Player}
 */
function enableOutroPlugin(player) {
    if (player && player.hasOwnProperty(OUTRO_PLUGIN)) {
        player[OUTRO_PLUGIN].enable();
    }
    return player;
}

/**
 * @private
 * @returns {string}
 */
function generateHtmlChildId() {
    return `bvp_playlist_${ Date.now() }`;
}

/**
 * @private
 * @param {function} accept
 * @param {function} callee
 */
function onTransitionEnd(accept, callee) {
    const originalHandler = callee();
    this.removeEventListener(TRANSITION_END, originalHandler);
    accept();
}

/**
 * @private
 * @param {object} item
 * @param {object} overwrites
 * @returns {object}
 */
function prepareItem(item, overwrites = {}) {
    return Object.assign({}, item, {
        htmlChildId: generateHtmlChildId()
    }, overwrites);
}

/**
 * @private
 * @param {videojs.Player} player
 */
function showTitleBar(player) {
    if (player && player.TitleBar) {
        player.TitleBar.show();
    }
}

/**
 * @private
 * @param {array} array
 * @param {number} start
 * @param {number} count
 * @returns {array}
 */
function cyclicSlice(array, start, count) {

    const items = [];
    const n = Math.min(array.length, count); // make sure we don't cycle more than once

    for (let i = 0; i < n; i++) {
        const indx = (start + i) % array.length;
        const item = array[indx];
        items.push(item);
    }
    return items;
}

export default PlaylistPlayer;
